import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { faCircleChevronLeft, faCircleChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { ProductsService } from '../../core/services/products.service';
import { CalendarDay } from '../../core/interfaces/calendar.interface';
import { CameraHeatmapSegment } from '../../core/interfaces/camera-heatmap.interface';
import { resolveGanttStatusForTask } from '../../core/utils/gantt-etapas.util';

@Component({
  selector: 'app-timeline',
  standalone: true,
  imports: [CommonModule, DatePipe, FontAwesomeModule, FormsModule, ReactiveFormsModule, TooltipModule],
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss']
})

export class TimelineComponent {
  @Input() blenders: any;
  @Input() packagingMachines: any;
  @Input() blenderTasks: any;
  @Input() packagingTasks: any
  @Input() isViewApo: boolean = false;
  @Input() calendar: any;
  @Input() selectedDate: Date = new Date();
  @Input() cameraHeatmapSegments: CameraHeatmapSegment[] = [];
  /** When true, the % ocupación row is always rendered (even if there are no segments for the day). */
  @Input() showCameraHeatmapRow = false;

  readonly cameraHeatmapTooltipOptions = { tooltipStyleClass: 'camera-heatmap-tooltip', escape: false };

  @Output() dateIncrement: EventEmitter<number> = new EventEmitter<number>();

  hours: number[] = [];
  startHour: number = 6;
  endHour: number = 22;
  faCircleChevronRight = faCircleChevronRight;
  faCircleChevronLeft = faCircleChevronLeft;
  isModalActive = false;
  isDisabled = true;

  selectedOrder: any;

  constructor(
    private productsSvc: ProductsService,
    private formBuilder: FormBuilder,
  ) { }

  ngOnInit() {
    this.updateHoursFromCalendar();
    if(this.blenders) {
      this.blenders = this.blenders.sort((a: any, b: any) => a.id - b.id);
    }
  }

  private updateHoursFromCalendar() {
    // Always use fixed hours 6-22 for timeline visualization
    this.startHour = 6;
    this.endHour = 22;
    
    const hourCount = this.endHour - this.startHour;
    this.hours = Array.from({ length: hourCount }, (_, i) => i + this.startHour);
  }

  private getCalendarDay(date: Date): CalendarDay | undefined {
    if (!this.calendar) return undefined;
    return this.calendar.find((day: CalendarDay) => {
      const calendarDate = new Date(day.date);
      return calendarDate.getDate() === date.getDate() && 
             calendarDate.getMonth() === date.getMonth() && 
             calendarDate.getFullYear() === date.getFullYear();
    });
  }

  ngOnChanges() {
    this.updateHoursFromCalendar();
    
    if (this.blenderTasks) {
      this.blenderTasks.forEach((task: any) => {
        const blenderDate = new Date(task.nueva_fecha_inicio_blender ? task.nueva_fecha_inicio_blender : task.fecha_inicio_blender ? task.fecha_inicio_blender : task.fecha_inicio);
        if (task.type === 'order') {
          task.hora_inicio_blender = blenderDate.getUTCHours() + blenderDate.getUTCMinutes() / 60;
        
        } else if (task.type === 'limpieza') {  
          task.hora_inicio = blenderDate.getUTCHours() + blenderDate.getUTCMinutes() / 60;
          }
        else if (task.type === 'calidad') {
          const qualityDate = new Date(task.nueva_fecha_inicio_calidad ? task.nueva_fecha_inicio_calidad : task.fecha_inicio_calidad ? task.fecha_inicio_calidad : task.fecha_inicio);
          task.hora_inicio_calidad = qualityDate.getUTCHours() + qualityDate.getUTCMinutes() / 60;
        }
      });
    }
    
    if (this.packagingTasks) {
      this.packagingTasks.forEach((task: any) => {
        if (task.type === 'order') {
          const packagingDate = new Date(task.nueva_fecha_inicio_envasadora ? task.nueva_fecha_inicio_envasadora : task.fecha_inicio_envasadora);
          task.hora_inicio_envasadora = packagingDate.getUTCHours()  + packagingDate.getUTCMinutes() / 60;
        }
      });
    }
  }

  onDateIncrement(change: number) {
    this.dateIncrement.emit(change);
  }

  openModal(item?: any) {
    this.selectedOrder = item;
    this.isModalActive = true;
  }

  closeModal() {
    this.isModalActive = false;
    this.selectedOrder = null;
  }

  trunc(value: number): number {
    return Math.trunc(value);
  }

  /**
   * The timeline grid has minute precision. A positive partial minute must
   * still occupy one column; a CSS Grid `span 0` is invalid and makes the
   * browser auto-place the task in the machine-label column.
   */
  gridSpan(minutes: number): number {
    return Math.max(1, Math.ceil(minutes));
  }

  getGanttNgClass(task: any, plannedClass: string): Record<string, boolean> {
    const status = resolveGanttStatusForTask(task);
    const hasEtapaOverride = status !== 'default';
    return {
      [plannedClass]: !!task.is_planned && !hasEtapaOverride,
      'gantt-etapa-completed':   status === 'completed',
      'gantt-etapa-current':     status === 'current',
      'gantt-etapa-not-started': status === 'not-started',
    };
  }

}
